/**
 * A cross-tab hint about whether a refresh cookie exists. The cookie itself is
 * HttpOnly, so this is the only way the client can avoid calling /auth/refresh
 * for visitors who are known to be anonymous. It is a hint, not a source of
 * truth: the server still decides whether the session is valid.
 *
 * Three states:
 *   absent  — nothing known yet (first visit, existing users before this code
 *             shipped, another origin of the same cookie domain): attempt once.
 *   "1"     — a session was established here.
 *   "0"     — a refresh was rejected here: skip until the next login.
 */
export const SESSION_HINT_KEY = "creatorverse:has-session";

interface HintStorage {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
	removeItem(key: string): void;
}

export interface SessionHint {
	/** False only when a refresh has been rejected in this browser since the last login. */
	mayHaveSession(): boolean;
	mark(): void;
	clear(): void;
}

export function createSessionHint(storage: HintStorage | null): SessionHint {
	const safely = <T>(fn: () => T, fallback: T): T => {
		if (!storage) return fallback;
		try {
			return fn();
		} catch {
			return fallback;
		}
	};
	return {
		mayHaveSession: () => safely(() => storage!.getItem(SESSION_HINT_KEY) !== "0", true),
		mark: () => safely(() => storage!.setItem(SESSION_HINT_KEY, "1"), undefined),
		clear: () => safely(() => storage!.setItem(SESSION_HINT_KEY, "0"), undefined),
	};
}

export const sessionHint = createSessionHint(typeof localStorage !== "undefined" ? localStorage : null);
